import { useEffect, useRef } from "react";
import { keyNameFromEvent } from "./keyNames.ts";

/**
 * Gmail / Linear-style key sequences: press the first key (`g`), then the
 * second within the timeout to fire the matching handler.
 *
 * One listener for the whole app rather than one per call site, because the
 * "a first key is pending" state has to be shared: two shortcuts that begin
 * with the same key must wait on the same timer, and a user can bind any
 * sequence to any shortcut, so no component knows the full set of leaders.
 * Call sites register through `useKeySequences`; `useShortcut` does so for
 * every registry shortcut whose effective keys include a sequence.
 */

const SEQUENCE_TIMEOUT_MS = 1200;

type SequenceEntry = {
  steps: string[];
  handler: (event: KeyboardEvent) => void;
};

const entries = new Set<SequenceEntry>();

let pending: string[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let listening = false;
let suspended = false;

const clearPending = () => {
  pending = [];
  if (timer) clearTimeout(timer);
  timer = undefined;
};

const isTypingTarget = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
};

const startsWith = (steps: string[], prefix: string[]): boolean =>
  prefix.length <= steps.length && prefix.every((step, i) => step === steps[i]);

/**
 * Stop sequences from firing until resumed. The shortcut recorder calls this
 * while it captures keys: both it and this layer listen on `window` in the
 * capture phase, and `stopPropagation` does not reach another listener on the
 * same node, so without this typing `g c` to record it would also navigate.
 */
export const setSequencesSuspended = (value: boolean): void => {
  suspended = value;
  if (value) clearPending();
};

const onKeyDown = (event: KeyboardEvent) => {
  if (suspended) return;
  // A modifier means the user asked for a chord; a text field owns its keys.
  if (isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) {
    clearPending();
    return;
  }
  const key = keyNameFromEvent(event);
  if (key === null) return;

  const candidate = [...pending, key];
  const matching = [...entries].filter((entry) => startsWith(entry.steps, candidate));
  const complete = matching.find((entry) => entry.steps.length === candidate.length);

  if (complete) {
    // Capture phase + stopPropagation so the final key is consumed before
    // react-hotkeys-hook's document-level listeners fire; otherwise a key that
    // is also a bare shortcut (`c` = agent, `t` = today) would double-fire.
    clearPending();
    event.preventDefault();
    event.stopPropagation();
    complete.handler(event);
    return;
  }
  if (matching.length > 0) {
    // A first key is never consumed: it may also be a bare shortcut in its own
    // right, and a conflict between the two is the editor's to warn about.
    clearPending();
    pending = candidate;
    timer = setTimeout(clearPending, SEQUENCE_TIMEOUT_MS);
    return;
  }
  clearPending();
};

const syncListener = () => {
  const shouldListen = entries.size > 0;
  if (shouldListen === listening) return;
  listening = shouldListen;
  if (shouldListen) {
    // Capture phase on window runs before any document-level bubble listener
    // (where react-hotkeys-hook binds), so a completed sequence is consumed first.
    window.addEventListener("keydown", onKeyDown, true);
  } else {
    window.removeEventListener("keydown", onKeyDown, true);
    clearPending();
  }
};

/**
 * Register `handler` for each sequence in `sequences` (each a list of bare key
 * names, e.g. `["g", "c"]`) while mounted and `enabled`. Sequences of one step
 * are not sequences; callers bind those as chords.
 */
export function useKeySequences(
  sequences: string[][],
  handler: (event: KeyboardEvent) => void,
  enabled: boolean,
): void {
  // Keyed by content so a re-render that rebuilds the array doesn't re-register,
  // and the handler is read through a ref for the same reason: call sites pass
  // inline closures, and the entry only has to reach the latest one.
  const sequenceKey = JSON.stringify(sequences);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const steps: string[][] = JSON.parse(sequenceKey);
    const registered = steps
      .filter((s) => s.length > 1)
      .map((s): SequenceEntry => ({ steps: s, handler: (event) => handlerRef.current(event) }));
    if (registered.length === 0) return;
    for (const entry of registered) entries.add(entry);
    syncListener();
    return () => {
      for (const entry of registered) entries.delete(entry);
      syncListener();
    };
  }, [sequenceKey, enabled]);
}
